import React, { useState, useEffect, useRef } from 'react';
import { useUser, UserData } from './hooks/useUser';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from './lib/utils';
import Home from './components/Home';
import Game from './components/Game';
import GameOver from './components/GameOver';
import Leaderboard from './components/Leaderboard';
import Friends from './components/Friends';
import Profile from './components/Profile';
import Settings from './components/Settings';
import ShellCrushGame from './components/ShellCrushGame';
import AppleGame from './components/AppleGame';
import GoldRushGame from './components/GoldRushGame';
import Missions from './components/Missions';
import LevelUpCelebration from './components/LevelUpCelebration';
import { App as CapacitorApp } from '@capacitor/app';
import SceneBackground from './components/SceneBackground';
import Shop from './components/Shop'; // Consolidated import for unified progression and account linking support
import DailyGiftModal from './components/DailyGiftModal';
import PrismManager from './components/PrismManager';
import Inventory from './components/Inventory';
// import FloatingBackground from './components/FloatingBackground';
import { useMissions } from './hooks/useMissions';
import { db } from './firebase';
import { calculatePrismBuffs } from './utils/prismSystem';
import { collection, addDoc, query, where, getDocs, orderBy, limit, doc, updateDoc } from 'firebase/firestore';
import { startOfMonth, endOfMonth, format } from 'date-fns';
import { Reward } from './utils/rewardSystem';
import RewardBoxModal from './components/RewardBoxModal';
import { audioEngine } from './utils/audioEngine';
import GameModal, { ModalType } from './components/GameModal';
import TutorialOverlay from './components/TutorialOverlay';
import { TUTORIAL_STEPS } from './utils/tutorialData';

import { Loader2 } from 'lucide-react';
import { adManager } from './utils/adManager';
import { purchaseManager } from './utils/purchaseManager';
import ChooseMode from './components/ChooseMode';
import BrandIntro from './components/BrandIntro';
import LoginScreen from './components/LoginScreen';

type Screen = 'home' | 'game' | 'sumgame' | 'applegame' | 'goldrush' | 'gameover' | 'leaderboard' | 'friends' | 'profile' | 'settings' | 'missions' | 'shop' | 'prism' | 'choose-mode' | 'dailydrop' | 'inventory';
type StartupStep = 'branding' | 'loading' | 'dashboard';

export interface GameStats {
  score: number;
  correctAnswers: number;
  mistakes: number;
  feverAnswers: number;
  feverTriggerCount: number;

  isWin?: boolean;
  bonusExp?: number;
}
export default function App() {
   const {
    user,
    loading,
    isDeletingAccount,
    consumeHeart,
    consumeStarTicket,
    addExp,
    sendHeartTicket,
    claimGift,
    secondsToNextHeart,
    updateNickname,
    addCoins,
    buyTicket,
    buyStarTicket,
    redeemTicket,
    refillHearts,
    restoreOneHeart,
    addTickets,
    addBadge,
    handleGameEnd,
    claimDailyGift,
    addRewards,
    addRewardBox,
    buyRewardBox,
    openRewardBox,
    setPremiumStatus,
    signInWithGoogle,
    logout,
    unboxPrism,
    equipPrism,
    evolvePrismsAction,
    rerollPrismAction,
    commitReroll,
    augmentPrismAction,
    commitAugment,
    togglePrismLock,
    addGems,
    buyAvatar,
    equipAvatar,
    grantMilestoneOfferRewards,
    expireMilestoneOffer,
    updateTutorialStep,
    completeTutorial,
    incrementAdCounter,
    grantTutorialPrisms,
    openAnimaTickets
  } = useUser();

  const [startupStep, setStartupStep] = useState<StartupStep>('branding');
  const [minLoadingDone, setMinLoadingDone] = useState(false);

  // Initialize Global Ads
  useEffect(() => {
    adManager.initialize();
  }, []);

  // Initialize Google Play Billing for Android
  useEffect(() => {
    if (user && purchaseManager.isAndroid()) {
      purchaseManager.initialize(user.uid).then(() => {
        purchaseManager.restorePurchases();
      });
    }
  }, [user?.uid]);
  const handleLevelUp = (result: any) => {
    if (result?.levelUps && result.levelUps.length > 0) {
      setLevelUpQueue(prev => [...prev, ...result.levelUps]);
    } else if (result?.leveledUp) {
      setLevelUpQueue(prev => [...prev, { level: result.newLevel, rewards: result.rewards }]);
    }
  };

  const addExpWrapped = async (amt: number, delayQueue?: boolean) => {
    const res = await addExp(amt);
    if (!delayQueue) {
      handleLevelUp(res);
    }
    return res;
  };

  const { updateProgress, totalClaimable } = useMissions(user, { addExp: addExpWrapped, addCoins, addGems, addTickets, addBadge, addRewardBox });
  const [currentScreen, setCurrentScreen] = useState<Screen>(() => {
    return (sessionStorage.getItem('nr_current_screen') as Screen) || 'home';
  });

  const [showMainMenu, setShowMainMenu] = useState(false);

  useEffect(() => {
    sessionStorage.setItem('nr_current_screen', currentScreen);
  }, [currentScreen]);

  const [isGameLoading, setIsGameLoading] = useState(false);

  // Dynamic BGM Screen State Router
  useEffect(() => {
    if (startupStep !== 'dashboard' || !user || isGameLoading) {
      audioEngine.stopMusic();
      return;
    }
    if (currentScreen === 'home' || currentScreen === 'choose-mode' || currentScreen === 'inventory' || currentScreen === 'missions' || currentScreen === 'dailydrop') {
      audioEngine.startMusic('menu');
    } else if (currentScreen === 'prism') {
      audioEngine.startMusic('anima');
    } else if (currentScreen === 'shop') {
      audioEngine.startMusic('shop');
    } else if (currentScreen === 'gameover') {
      audioEngine.stopMusic();
    }
  }, [currentScreen, startupStep, user, isGameLoading]);
   const [lastScore, setLastScore] = useState(0);
  const [expGained, setExpGained] = useState(0);
  const [gemsGained, setGemsGained] = useState(0);
  const [coinsGained, setCoinsGained] = useState(0);
  const [gameMode, setGameMode] = useState<'pemdas' | 'shellcrush' | 'starburst' | 'goldrush'>('pemdas');
  const [isPractice, setIsPractice] = useState(false);
  const [gameOverStats, setGameOverStats] = useState<{
    rank: number;
    isMonthlyBest: boolean;
    isAllTimeBest: boolean;
 
  }>({ rank: 0, isMonthlyBest: false, isAllTimeBest: false });
  const [levelUpQueue, setLevelUpQueue] = useState<{ level: number, rewards: { type: string, amount: number | string }[] }[]>([]);
  const [shopTab, setShopTab] = useState<'market' | 'bank'>('market');
  const [openingBox, setOpeningBox] = useState<keyof UserData['rewardBoxes'] | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
  const [lastStats, setLastStats] = useState<GameStats | null>(null);
  const [modal, setModal] = useState<{ open: boolean; title: string; message: string; type: ModalType; rewards?: any[] }>({
    open: false,
    title: '',
    message: '',
    type: 'info',
    rewards: undefined
  });
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [news, setNews] = useState<any[]>([]);
  const [prismConfig, setPrismConfig] = useState<{ mode: 'pemdas'|'shellcrush'|'goldrush'|null, returnScreen: Screen }>({ mode: null, returnScreen: 'home' });

  // Fetch news from Firestore on mount
  useEffect(() => {
    const fetchNews = async () => {
      try {
        const q = query(collection(db, 'news'), orderBy('timestamp', 'desc'));
        const querySnapshot = await getDocs(q);
        const fetchedNews = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setNews(fetchedNews);
      } catch (error) {
        console.error("Error fetching news from Firestore:", error);
      }
    };
    fetchNews();
  }, []);

  // Tutorial Auto-Start & Screen Sync
  useEffect(() => {
    if (user && !user.isTutorialCompleted && startupStep === 'dashboard') {
      // Auto-start step 1
      if (user.tutorialStep === 0) {
        updateTutorialStep(1);
        return;
      }

      // Sync Screen
      const data = TUTORIAL_STEPS[user.tutorialStep];
      if (data && data.screen && currentScreen !== data.screen) {
        setCurrentScreen(data.screen as any);
      }
    }
  }, [user, startupStep, currentScreen, updateTutorialStep]);

  // Auto-grant tutorial prisms if user is on step 37 but has not received them yet
  useEffect(() => {
    if (user && user.tutorialStep === 37 && !user.isTutorialCompleted && !user.hasGrantedWelcomeCores) {
      grantTutorialPrisms();
    }
  }, [user, grantTutorialPrisms]);

  // Auto-advance tutorial if username is already set at step 36
  useEffect(() => {
    if (user && user.tutorialStep === 36 && user.nickname !== null && !user.isTutorialCompleted) {
      const t = setTimeout(() => {
        updateTutorialStep(37);
      }, 2500);
      return () => clearTimeout(t);
    }
  }, [user?.tutorialStep, user?.nickname, user?.isTutorialCompleted, updateTutorialStep]);

  const handleNextTutorialStep = async () => {
    if (user) {
      let nextStep = user.tutorialStep + 1;
      if (user.tutorialStep === 31) {
        nextStep = 33;
      }
      
      const markModeComplete = async (mode: string) => {
        const currentModes = user.completedStandardModes || [];
        if (!currentModes.includes(mode)) {
          const updatedModes = [...currentModes, mode];
          const userDocRef = doc(db, 'users', user.uid);
          await updateDoc(userDocRef, { completedStandardModes: updatedModes });
        }
      };

      // Milestone Rewards
      if (user.tutorialStep === 13) {
        // Finished Shell Crush
        await markModeComplete('shellcrush');
        await updateProgress('tutorial_shellcrush', 1);
        showAlert(
          "TUTORIAL COMPLETE",
          "You have finished Shell Crush training!\nClaim your rewards under Lifetime Missions.",
          "success"
        );
        nextStep = 14;
        setCurrentScreen('choose-mode');
      } else if (user.tutorialStep === 21) {
        // Finished Gold Rush
        await markModeComplete('goldrush');
        await updateProgress('tutorial_goldrush', 1);
        showAlert(
          "TUTORIAL COMPLETE",
          "You have finished Gold Rush training!\nClaim your rewards under Lifetime Missions.",
          "success"
        );
        nextStep = 22;
        setCurrentScreen('choose-mode');
      } else if (user.tutorialStep === 28) {
        // Finished Cloud Mach
        await markModeComplete('pemdas');
        await updateProgress('tutorial_pemdas', 1);
        showAlert(
          "TUTORIAL COMPLETE",
          "You have finished Cloud Mach training!\nClaim your rewards under Lifetime Missions.",
          "success"
        );
        nextStep = 29;
        setCurrentScreen('choose-mode');
      } else if (user.tutorialStep === 35) {
        // Finished Star Burst
        await markModeComplete('starburst');
        await updateProgress('tutorial_starburst', 1);
        showAlert(
          "TUTORIAL COMPLETE",
          "You have finished Star Burst training!\nClaim your rewards under Lifetime Missions.",
          "success"
        );
        nextStep = 36;
        setCurrentScreen('home');
      } else if (user.tutorialStep === 44) {
        nextStep = 45;
        setCurrentScreen('home');
      } else if (user.tutorialStep === 45) {
        // Tutorial Complete
        await completeTutorial();
        await updateProgress('tutorial_graduation', 1);
        showAlert(
          "GRADUATION REWARDS",
          "Congratulations on graduating from the tutorial! Claim your Graduation rewards under Lifetime Missions.",
          "success"
        );
        nextStep = 999;
        setCurrentScreen('home');
        return;
      }

      if (nextStep === 37) {
        await grantTutorialPrisms();
      }

      // Safeguards for previous linear flow
      if (nextStep === 999) {
        await completeTutorial();
        setCurrentScreen('home');
        return;
      }

      updateTutorialStep(nextStep);
    }
  };

  const handlePrevTutorialStep = async () => {
    if (user && user.tutorialStep > 1) {
      let prevStep = user.tutorialStep - 1;
      if (user.tutorialStep === 33) {
        prevStep = 31;
      }
      const data = TUTORIAL_STEPS[prevStep];
      if (data && data.screen) {
        setCurrentScreen(data.screen as any);
      }
      updateTutorialStep(prevStep);
    }
  };

  const handleResetTutorial = async () => {
    if (user) {
      await updateTutorialStep(29);
      setCurrentScreen('choose-mode');
    }
  };

  const handleSkipTutorial = async () => {
    if (user) {
      await completeTutorial();
      setCurrentScreen('home');
    }
  };

  const handleNextMission = async () => {
    if (user) {
      const step = user.tutorialStep;
      let nextStep = 999;
      if (step < 14) {
        nextStep = 14;
        setCurrentScreen('choose-mode');
      } else if (step < 22) {
        nextStep = 22;
        setCurrentScreen('choose-mode');
      } else if (step < 29) {
        nextStep = 29;
        setCurrentScreen('choose-mode');
      } else if (step < 37) {
        nextStep = 37;
        await grantTutorialPrisms();
        setCurrentScreen('prism');
      } else {
        nextStep = 48;
      }

      if (nextStep === 999) {
        await completeTutorial();
        setCurrentScreen('home');
        return;
      }

      updateTutorialStep(nextStep);
    }
  };
  const [showExitAppConfirm, setShowExitAppConfirm] = useState(false);

  const activePrismId = user?.equippedPrismIds?.[gameMode as 'pemdas' | 'shellcrush' | 'goldrush'] || user?.equippedPrismId;
  const equippedPrism = user?.prismInventory?.find(p => p.id === activePrismId);
  const prismBuffs = (currentScreen === 'applegame' || gameMode === 'starburst') ? calculatePrismBuffs(undefined) : calculatePrismBuffs(equippedPrism);

  const showAlert = (title: string, message: string, type: ModalType = 'info', rewards?: any[]) => {
    setModal({ open: true, title, message, type, rewards });
  };

  // Force Default Dark Theme
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'default');
  }, []);

  // Auth State Watcher: Transition back to loading screen (Login) on logout/delete
  useEffect(() => {
    if (!loading && !user) {
      setStartupStep('loading');
      setMinLoadingDone(false);
      // Reset all screen/modal/game state so nothing lingers after deletion or logout
      setCurrentScreen('home');
      setShowMainMenu(false);
      setOpeningBox(null);
      setLevelUpQueue([]);
      setModal({ open: false, title: '', message: '', type: 'info', rewards: undefined });
      setShowBackConfirm(false);
      setShowExitAppConfirm(false);
      setIsGameLoading(false);
      setSaveStatus('idle');
      setLastStats(null);
      setShopTab('market');
      setPrismConfig({ mode: null, returnScreen: 'home' });
      setIsPractice(false);
    }
  }, [user, loading]);

  // Hardware Back Button & App State Lifecycle Interceptors
  useEffect(() => {
    // 1. App State Listener (Pause music when minimized/backgrounded)
    const stateListener = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        audioEngine.suspendAudio();
      } else {
        audioEngine.resumeAudio();
      }
    });

    // 2. Hardware Back Button Listener
    const handleBackButton = ({ canGoBack }: any) => {
      // Tutorial logic: Do not allow backing out of tutorial steps to prevent breaking flow
      if (user && !user.isTutorialCompleted && user.tutorialStep > 0) {
        return; 
      }

      const gameScreens = ['game', 'sumgame', 'applegame', 'goldrush'];
      const subScreens = ['leaderboard', 'friends', 'profile', 'settings', 'missions', 'shop', 'prism', 'choose-mode', 'dailydrop', 'inventory'];

      if (gameScreens.includes(currentScreen)) {
        setShowBackConfirm(true);
      } else if (currentScreen === 'home') {
        setShowExitAppConfirm(true);
      } else if (subScreens.includes(currentScreen)) {
        setCurrentScreen('home');
      } else if (canGoBack) {
        window.history.back();
      }
    };

    const backListener = CapacitorApp.addListener('backButton', handleBackButton);

    // Fallback: Web PWA Popstate Listener
    window.history.pushState(null, '', window.location.href);
    const handlePopState = (event: PopStateEvent) => {
      window.history.pushState(null, '', window.location.href);
      handleBackButton({ canGoBack: false });
    };
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      stateListener.then(l => l.remove());
      backListener.then(l => l.remove());
    };
  }, [currentScreen, user]);

  // ── STARTUP SEQUENCE LOGIC ──────────────────────────────────────────────────
  const loadingRef = useRef(loading);
  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    const runStartup = async () => {
      // 1. Brand Page (The "1.5s Hold")
      await new Promise(resolve => setTimeout(resolve, 1500));
      setStartupStep('loading');

      // 2. High-end Game loading experience: Mimic Pikmin Bloom / Premium titles
      // We wait for BOTH the real Firebase data AND a mandatory 2-second timer.
      const timerPromise = new Promise(resolve => setTimeout(resolve, 2000));
      
      const authReadyPromise = new Promise(resolve => {
        const check = () => {
          if (!loadingRef.current) resolve(true);
          else requestAnimationFrame(check);
        };
        check();
      });

      await Promise.all([timerPromise, authReadyPromise]);
      
      // 1 second hold after bar completes before allowing dashboard transition
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      setMinLoadingDone(true);
    };
    runStartup();
  }, []);



  // Re-enable login screen buttons after logout/delete resets minLoadingDone
  useEffect(() => {
    if (startupStep === 'loading' && !minLoadingDone && !loading) {
      const t = setTimeout(() => setMinLoadingDone(true), 3200);
      return () => clearTimeout(t);
    }
  }, [startupStep, minLoadingDone, loading]);

  const handleStartGame = async (mode: 'pemdas' | 'shellcrush' | 'starburst' | 'goldrush' = 'pemdas', practice: boolean = false) => {
    if (!user) return;

    // Immediately stop menu music upon launching a game
    audioEngine.stopMusic(250);

    setIsPractice(practice);

    if (!practice) {
      if (mode === 'starburst') {
        const isTutorialBypass = !user.isTutorialCompleted && user.tutorialStep === 33;
        if ((user.starTickets || 0) <= 0 && !isTutorialBypass) {
          audioEngine.playNotEnough();
          showAlert("NO STAR TICKETS", "You need a Star Ticket to play Star Burst. Currently, these cannot be obtained.", "warning");
          return;
        }
        if (!isTutorialBypass) {
          const success = await consumeStarTicket();
          if (!success) {
            showAlert("NO STAR TICKETS", "You need a Star Ticket to play Star Burst. Currently, these cannot be obtained.", "warning");
            return;
          }
        }
      } else {
        if (user.isTutorialCompleted) {
          const success = await consumeHeart();
          if (!success) {
            audioEngine.playNotEnough();
            showAlert("OUT OF HEARTS", "Failed to consume heart.", "error");
            return;
          }
        }
      }
    }

    // Tutorial Intercept
    if (!user.isTutorialCompleted && user.tutorialStep === 5) {
      if (mode === 'shellcrush') await updateTutorialStep(7);
      else if (mode === 'goldrush') await updateTutorialStep(15);
      else if (mode === 'pemdas') await updateTutorialStep(23);
      else if (mode === 'starburst') await updateTutorialStep(34);
    }

    const startTransition = () => {
      setGameMode(mode);
      if (mode === 'pemdas') setCurrentScreen('game');
      else if (mode === 'shellcrush') setCurrentScreen('sumgame');
      else if (mode === 'starburst') setCurrentScreen('applegame');
      else if (mode === 'goldrush') setCurrentScreen('goldrush');
    };

    if (user.isTutorialCompleted || user.tutorialStep >= 46) {
      setIsGameLoading(true);
      setTimeout(() => {
        setIsGameLoading(false);
        startTransition();
      }, 800);
    } else {
      startTransition();
    }
  };

  const handleGameOver = async (stats: GameStats, forceReal: boolean = false) => {
    const activePrismId = user?.equippedPrismIds?.[gameMode as 'pemdas' | 'shellcrush' | 'goldrush'] || user?.equippedPrismId;
    const equippedPrism = user?.prismInventory?.find(p => p.id === activePrismId);
    const buffs = gameMode === 'starburst' ? calculatePrismBuffs(undefined) : calculatePrismBuffs(equippedPrism);
    const finalScore = Math.floor(stats.score * (1 + buffs.scoreMultiplier));

    // 1. Instant UI Transition
    setLastScore(finalScore);
    setLastStats(stats);
    setCurrentScreen('gameover');

    const actuallyPractice = forceReal ? false : isPractice;

    if (user && !actuallyPractice) {
      // Award coins (logic handles buffs internally now)
      setExpGained(0); 

      // Save score to leaderboard (only if score > 0)
      if (finalScore > 0) {
        setSaveStatus('saving');
        addDoc(collection(db, 'scores'), {
          uid: user.uid,
          displayName: user.nickname || user.displayName,
          photoURL: user.photoURL,
          score: finalScore,
          mode: gameMode,
          level: user.level,
          monthLabel: format(new Date(), 'yyyy-MM'),
          timestamp: new Date().toISOString(),
          avatarId: user.avatarId || 'g1'
        }).then(() => {
          console.log("Leaderboard save successful");
          setSaveStatus('success');
        }).catch(e => {
          console.error("Leaderboard save failed:", e);
          setSaveStatus('error');
        });
      }

      // Handle game end (EXP, Level Up, Rewards)
      handleGameEnd({ ...stats, score: finalScore, rawScore: stats.score, mode: gameMode as 'pemdas' | 'shellcrush' | 'starburst' | 'goldrush', bonusExp: (stats as any).bonusExp || 0 }).then(result => {
        if (result) {
          setExpGained(result.earnedExp);
          setGemsGained((result as any).earnedGems || 0);
          setCoinsGained((result as any).earnedCoins || 0);
          handleLevelUp(result);
        }
      }).catch(e => console.error("Game end logic failed:", e));

      // Update Missions
      updateProgress('warm_up', 1);
      updateProgress('the_grinder', 1);
      updateProgress('marathon_monthly', 1);
      updateProgress('high_scorer_daily', finalScore);

      if (stats.feverTriggerCount > 0) {
        updateProgress('brain_burner', stats.feverTriggerCount);
        updateProgress('adrenaline_junkie', stats.feverTriggerCount);
        updateProgress('pro_fever_marathon', stats.feverTriggerCount);
      }

      if (gameMode === 'pemdas') {
        updateProgress('cloudmach_expert', 1);
        updateProgress('cloudmach_expert_2', 1);
        updateProgress('cloudmach_expert_3', 1);
      } else if (gameMode === 'shellcrush') {
        updateProgress('shellcrush_expert', 1);
        updateProgress('shellcrush_expert_2', 1);
        updateProgress('shellcrush_expert_3', 1);
      } else if (gameMode === 'starburst') {
        updateProgress('starburst_challenger', 1);
      } else if (gameMode === 'goldrush') {
        updateProgress('goldrush_expert', 1);
        updateProgress('goldrush_expert_2', 1);
        updateProgress('goldrush_expert_3', 1);
      }

      if (gameMode === 'pemdas' || gameMode === 'shellcrush' || gameMode === 'goldrush') {
        updateProgress('pro_grand_tour', 1);
      }

      // Morning Coffee check
      const hour = new Date().getHours();
      if (hour < 10) {
        updateProgress('morning_coffee', 1);
      }

      // Calculate rank and bests in background
      try {
        const monthLabel = format(new Date(), 'yyyy-MM');
        const monthlyQ = query(
          collection(db, 'scores'),
          where('mode', '==', gameMode),
          where('monthLabel', '==', monthLabel),
          orderBy('score', 'desc'),
          limit(100)
        );
        const monthlySnap = await getDocs(monthlyQ);
        const monthlyScores = monthlySnap.docs.map(d => d.data());

        // Find best monthly score for THIS user
        const userMonthlyScores = monthlyScores.filter(s => s.uid === user.uid);
        const previousMonthlyBest = userMonthlyScores.length > 1
          ? Math.max(...userMonthlyScores.filter(s => s.score !== stats.score).map(s => s.score), 0)
          : 0;

        // Calculate rank among unique users
        const uniqueUserBestScores: Record<string, number> = {};
        monthlyScores.forEach(s => {
          if (!uniqueUserBestScores[s.uid] || s.score > uniqueUserBestScores[s.uid]) {
            uniqueUserBestScores[s.uid] = s.score;
          }
        });
        const sortedUniqueScores = Object.values(uniqueUserBestScores).sort((a, b) => b - a);
        const rank = sortedUniqueScores.indexOf(finalScore) + 1;

        // All time check
        const allTimeQ = query(
          collection(db, 'scores'),
          where('mode', '==', gameMode),
          where('uid', '==', user.uid),
          orderBy('score', 'desc'),
          limit(2)
        );
        const allTimeSnap = await getDocs(allTimeQ);
        const previousAllTimeBest = allTimeSnap.docs.length > 1
          ? allTimeSnap.docs[1].data().score
          : 0;

        setGameOverStats({
          rank: rank || sortedUniqueScores.length + 1,
          isMonthlyBest: stats.score > previousMonthlyBest,
          isAllTimeBest: stats.score > previousAllTimeBest
        });
      } catch (err) {
        console.error("GameOver stats background calculation error:", err);
      }
    }
  };

  const handleClaimPractice = async () => {
    if (!user || !lastStats || gameMode !== 'starburst') return;
    
    const success = await consumeStarTicket();
    if (success) {
      setIsPractice(false);
      await handleGameOver(lastStats, true);
      const exp = lastStats.score * 1000;
      const gems = lastStats.score * 100;
      showAlert("CLAIMED", "Practice results claimed! Your rewards and score have been saved.", "success", [
        { type: 'gems', amount: gems },
        { type: 'exp', amount: exp }
      ]);
    } else {
      showAlert("NO TICKETS", "You need a Star Ticket to claim rewards.", "warning");
    }
  };

  const handleSendHeart = async (friendUid: string, message: string = '') => {
    const success = await sendHeartTicket(friendUid, message);
    if (success) {
      updateProgress('social_butterfly', 1);
      updateProgress('philanthropist', 1);
      showAlert("SUCCESS", "Heart ticket sent to your friend!", "success");
      return true;
    } else {
      showAlert("COOLDOWN", "You can only send a ticket every 6 hours!", "info");
      return false;
    }
  };

  return (
    <div className={cn(
      "w-full max-w-md md:max-w-xl lg:max-w-2xl aspect-[9/19] h-auto min-h-dvh mx-auto overflow-hidden shadow-2xl relative transition-colors duration-1000 bg-[#020617] game-container",
      "pt-[max(env(safe-area-inset-top),_16px)] pb-[max(env(safe-area-inset-bottom),_16px)]"
    )}>
      {/* Account Deletion Guard Overlay */}
      {isDeletingAccount && (
        <div className="absolute inset-0 z-[10000] flex flex-col items-center justify-center bg-slate-950/95 backdrop-blur-md">
          <Loader2 className="w-10 h-10 animate-spin text-amber-500 mb-4" />
          <p className="text-white font-num text-xs font-black uppercase tracking-widest">Deleting Account...</p>
        </div>
      )}

      {/* Startup Sequence */}
      <AnimatePresence mode="wait">
        {startupStep === 'branding' ? (
          <motion.div 
            key="branding"
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="absolute inset-0 z-[600]"
          >
            <BrandIntro onComplete={() => {}} />
          </motion.div>
        ) : startupStep === 'loading' ? (
          <motion.div 
            key="loading"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="absolute inset-0 z-[500]"
          >
            <LoginScreen 
              onSignInGoogle={signInWithGoogle} 
              hideButtons={!minLoadingDone || loading || !!user}
              isLoading={!minLoadingDone}
              isReadyToPlay={startupStep === 'loading' && minLoadingDone && !loading && !!user}
              onContinue={() => {
                audioEngine.playSound('click');
                setStartupStep('dashboard');
              }}
            />
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="relative z-10 h-full flex flex-col">
        <AnimatePresence mode="wait">
          {(currentScreen === 'home' || currentScreen === 'choose-mode') && startupStep === 'dashboard' && (
            <motion.div
              key="dashboard"
              layout="position"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.2 }}
              className="flex-1 min-h-0 flex flex-col main-lobby-wrapper"
            >
              <Home
                user={user}
                news={news}
                lastScore={lastScore}
                secondsToNextHeart={secondsToNextHeart}
                onStartGame={(mode) => handleStartGame(mode)}
                onShowLeaderboard={() => setCurrentScreen('leaderboard')}
                onShowFriends={() => setCurrentScreen('friends')}
                onShowProfile={() => setCurrentScreen('profile')}
                onShowSettings={() => setCurrentScreen('settings')}
                onUpdateNickname={async (nickname) => {
                  const res = await updateNickname(nickname);
                  if (res && res.success && user && user.tutorialStep === 36) {
                    setTimeout(() => {
                      updateTutorialStep(37);
                    }, 2500);
                  }
                  return res;
                }}
                onBuyTicket={buyTicket}
                onRedeemTicket={redeemTicket}
                onAddTickets={addTickets}
                onShowMissions={() => setCurrentScreen('missions')}
                onUpdateMission={updateProgress}
                onClaimDailyGift={claimDailyGift}
                onOpenBox={(tier) => setOpeningBox(tier)}
                onBuyBox={buyRewardBox}
                onShowShop={(tab) => {
                  if (tab) setShopTab(tab);
                  setCurrentScreen('shop');
                }}
                onSignInGoogle={signInWithGoogle}
                onSetPremiumStatus={setPremiumStatus}
                onShowAlert={showAlert}
                unboxPrism={unboxPrism}
                onShowPrismManager={(mode?: any) => {
                  setPrismConfig({ mode: mode as any || null, returnScreen: 'home' });
                  setCurrentScreen('prism');
                }}
                onShowChooseMode={() => setCurrentScreen('choose-mode')}
                onShowDailyDrop={() => setCurrentScreen('dailydrop')}
                totalClaimableMissions={totalClaimable}
                onShowInventory={() => setCurrentScreen('inventory')}
                hideBottomNav={currentScreen === 'choose-mode'}
                onExpireMilestoneOffer={expireMilestoneOffer}
                onGrantMilestoneOfferRewards={grantMilestoneOfferRewards}
                showMainMenu={showMainMenu}
                onShowMainMenuChange={setShowMainMenu}
              />
            </motion.div>
          )}

      {currentScreen === 'game' && (
        <motion.div
          key="game"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <Game
            onGameOver={handleGameOver}
            onBack={() => setCurrentScreen('home')}
            onUpdateMission={isPractice ? undefined : updateProgress}
            prismBuffs={prismBuffs}
            tutorialStep={user?.tutorialStep}
            onNextTutorialStep={handleNextTutorialStep}
          />
        </motion.div>
      )}

      {currentScreen === 'sumgame' && (
        <motion.div
          key="sumgame"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <ShellCrushGame
            onGameOver={handleGameOver}
            onBack={() => setCurrentScreen('home')}
            onUpdateMission={isPractice ? undefined : updateProgress}
            prismBuffs={prismBuffs}
            highScore={user?.highScores?.shellcrush || 0}
            tutorialStep={user?.tutorialStep}
            onNextTutorialStep={handleNextTutorialStep}
          />
        </motion.div>
      )}

      {currentScreen === 'applegame' && (
        <motion.div
          key="applegame"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <AppleGame
            onGameOver={handleGameOver}
            onBack={() => setCurrentScreen('home')}
            onUpdateMission={isPractice ? undefined : updateProgress}
            prismBuffs={prismBuffs}
            isPractice={isPractice}
            tutorialStep={user?.tutorialStep}
            onNextTutorialStep={handleNextTutorialStep}
          />
        </motion.div>
      )}

      {currentScreen === 'goldrush' && (
        <motion.div
          key="goldrush"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <GoldRushGame 
            onBack={() => setCurrentScreen('home')}
            onGameOver={handleGameOver}
            onUpdateMission={isPractice ? undefined : updateProgress}
            prismBuffs={prismBuffs}
            highScore={user?.highScores?.goldrush || 0}
            tutorialStep={user?.tutorialStep}
            onNextTutorialStep={handleNextTutorialStep}
          />
        </motion.div>
      )}

      {currentScreen === 'gameover' && (
        <motion.div
          key="gameover"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <GameOver 
            score={lastScore} 
            level={user?.level || 1} 
            expGained={expGained}
            gemsGained={gemsGained}
            coinsGained={coinsGained}
            rank={gameOverStats.rank}
            isMonthlyBest={gameOverStats.isMonthlyBest}
            isAllTimeBest={gameOverStats.isAllTimeBest}
            onRestart={() => handleStartGame(gameMode, isPractice)}
            onHome={() => setCurrentScreen('home')}
            mode={gameMode}
            prismBuffs={prismBuffs}
            saveStatus={saveStatus}
            isPractice={isPractice}
            onClaimPractice={handleClaimPractice}
            starTickets={user?.starTickets || 0}
            equippedPrism={equippedPrism}
          />
        </motion.div>
      )}

      {currentScreen === 'leaderboard' && (
        <motion.div
          key="leaderboard"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <Leaderboard onBack={() => setCurrentScreen('home')} />
        </motion.div>
      )}

      {currentScreen === 'friends' && user && (
        <motion.div
          key="friends"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <Friends
            user={user}
            onBack={() => setCurrentScreen('home')}
            onSendHeart={handleSendHeart}
            onClaimGift={claimGift}
          />
        </motion.div>
      )}

      {currentScreen === 'profile' && user && (
        <motion.div
          key="profile"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <Profile
            user={user}
            onBack={() => setCurrentScreen('home')}
            onUpdateNickname={updateNickname}
            onSignIn={async () => { await signInWithGoogle(); }}
            onLogout={async () => { await logout(); setCurrentScreen('home'); }}
            onEquipAvatar={equipAvatar}
          />
        </motion.div>
      )}

      {currentScreen === 'settings' && (
        <motion.div
          key="settings"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <Settings onBack={() => setCurrentScreen('home')} />
        </motion.div>
      )}

      {currentScreen === 'missions' && (
        <motion.div
          key="missions"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.2 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <Missions
            user={user}
            onBack={() => setCurrentScreen('home')}
            addExp={addExpWrapped}
            addCoins={addCoins}
            addGems={addGems}
            addTickets={addTickets}
            addBadge={addBadge}
            addRewardBox={addRewardBox}
            onClaimDailyGift={async (double) => await claimDailyGift('basic', double)}
            onQueueLevelUp={handleLevelUp}
            onSetPremiumStatus={setPremiumStatus}
          />
        </motion.div>
      )}

      {currentScreen === 'dailydrop' && user && (
        <motion.div
          key="dailydrop"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <DailyGiftModal
            user={user}
            onClose={() => setCurrentScreen('home')}
            onClaim={claimDailyGift}
            onUpgrade={() => {
              setCurrentScreen('shop');
            }}
          />
        </motion.div>
      )}

      {currentScreen === 'shop' && user && (
        <motion.div
          key="shop"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <Shop
            user={user}
            onBack={() => setCurrentScreen('home')}
            onBuyBox={buyRewardBox}
            onBuyTicket={buyTicket}
            onBuyStarTicket={buyStarTicket}
            onAddTickets={addTickets}
            onUpdateMission={updateProgress}
            onOpenBox={(tier) => setOpeningBox(tier)}
            initialTab={shopTab}
            onBuyAvatar={buyAvatar}
            onGrantMilestoneOfferRewards={grantMilestoneOfferRewards}
            onExpireMilestoneOffer={expireMilestoneOffer}
            onRefillHearts={refillHearts}
            onIncrementAdCounter={incrementAdCounter}
            onSetPremiumStatus={setPremiumStatus}
          />
        </motion.div>
      )}

      {currentScreen === 'prism' && user && (
        <motion.div
          key="prism"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <PrismManager 
             user={user}
             initialEquipMode={prismConfig.mode}
             onClose={() => setCurrentScreen(prismConfig.returnScreen)}
             onEquip={equipPrism}
             onEvolve={async (ids) => {
               const res = await evolvePrismsAction(ids);
               if (res) updateProgress('pro_prism_refiner', 1);
               return res;
             }}
             onReroll={rerollPrismAction}
             onCommitReroll={async (prismId, fodderIds, kept) => {
               await commitReroll(prismId, fodderIds, kept);
               updateProgress('pro_prism_refiner', 1);
             }}
             onAugment={augmentPrismAction}
             onCommitAugment={commitAugment}
             onShowAlert={showAlert}
             onToggleLock={togglePrismLock}
             tutorialStep={user.tutorialStep}
             onNextTutorialStep={handleNextTutorialStep}
          />
        </motion.div>
      )}
      {currentScreen === 'inventory' && user && (
        <motion.div
          key="inventory"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.2 }}
          className="flex-1 min-h-0 flex flex-col"
        >
          <Inventory
            user={user}
            onClose={() => setCurrentScreen('home')}
            onOpenBox={(tier) => setOpeningBox(tier)}
            onRedeemTicket={redeemTicket}
            onShowPrismManager={(mode?: any) => {
              setPrismConfig({ mode: mode as any || null, returnScreen: 'inventory' });
              setCurrentScreen('prism');
            }}
            onOpenAnimaTickets={openAnimaTickets}
            onBuyStarTicket={buyStarTicket}
          />
        </motion.div>
      )}
    </AnimatePresence>

      {/* ChooseMode Modal Overlay */}
      <AnimatePresence>
        {currentScreen === 'choose-mode' && user && (
          <motion.div
            key="choose-mode"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 z-[999]"
          >
            <ChooseMode 
              user={user}
              onBack={() => setCurrentScreen('home')}
              onStartGame={(mode, practice) => handleStartGame(mode, practice)}
              onShowAlert={showAlert}
              onShowPrismManager={(mode) => {
                setPrismConfig({ mode: mode as any || null, returnScreen: 'choose-mode' });
                setCurrentScreen('prism');
              }}
              onRedeemTicket={redeemTicket}
              onShowShop={() => setCurrentScreen('shop')}
              onIncrementAdCounter={incrementAdCounter}
              onNextTutorialStep={handleNextTutorialStep}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reward Box Opening Modal Overlay */}
      {openingBox && user && (
        <RewardBoxModal
          tier={openingBox}
          maxCount={user.rewardBoxes[openingBox]}
          onClose={() => {
            console.log("RewardBoxModal onClose triggered! Setting openingBox to null.");
            setOpeningBox(null);
          }}
          onClaim={async (rewards, count) => {
            if (openingBox) {
              await openRewardBox(openingBox, count);
              await addRewards(rewards);
              
              // Track box opening missions
              updateProgress('window_shopper', count);
              updateProgress('unboxer_weekly', count);
              updateProgress('pro_box_scavenger', count);
              if (openingBox === 'bronze') {
                updateProgress('bronze_of_wisdom', count);
              } else if (openingBox === 'gold') {
                updateProgress('box_hoarder', count);
              } else if (openingBox === 'diamond') {
                updateProgress('box_crusher', count);
                updateProgress('pro_diamond_hoarder', count);
              }
            }
          }}
        />
      )}
      {levelUpQueue.length > 0 && (
        <LevelUpCelebration
          key={`levelup-${levelUpQueue[0].level}`}
          level={levelUpQueue[0].level}
          rewards={levelUpQueue[0].rewards}
          onClose={() => setLevelUpQueue(prev => prev.slice(1))}
        />
      )}

      <GameModal 
        isOpen={modal.open}
        onClose={() => setModal({ ...modal, open: false })}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        rewards={modal.rewards}
      />



      <GameModal
        isOpen={showBackConfirm}
        onClose={() => setShowBackConfirm(false)}
        onConfirm={() => {
          setShowBackConfirm(false);
          setCurrentScreen('home');
        }}
        type="confirm"
        title="QUIT GAME?"
        message="Are you sure you want to quit the current game? Progress will be lost."
        confirmLabel="QUIT"
        cancelLabel="STAY"
      />

      <GameModal
        isOpen={showExitAppConfirm}
        onClose={() => setShowExitAppConfirm(false)}
        onConfirm={() => {
          CapacitorApp.exitApp();
          setShowExitAppConfirm(false);
        }}
        type="confirm"
        title="EXIT APP?"
        message="Do you want to leave NumQ?"
        confirmLabel="EXIT"
        cancelLabel="STAY"
      />
      
      <AnimatePresence>
        {isGameLoading && (
          <motion.div 
            className="fixed inset-0 bg-black z-[9999] pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="absolute bottom-10 right-10 flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 border-slate-800 border-t-white rounded-full animate-spin drop-shadow-md" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      </div>
      {/* Tutorial Overlay */}
      {user && !user.isTutorialCompleted && user.tutorialStep > 0 && startupStep === 'dashboard' && !modal.open && !showBackConfirm && !showExitAppConfirm && !openingBox && (
        <TutorialOverlay 
          step={user.tutorialStep} 
          onNext={handleNextTutorialStep} 
          onReset={handleResetTutorial}
          onBack={handlePrevTutorialStep}
          currentScreen={currentScreen} 
          onSkip={handleSkipTutorial}
          onNextMission={handleNextMission}
          user={user}
        />
      )}
    </div>
  );
}
